import styled, { css } from 'styled-components'

const TRANSITION = '220ms ease'

const SEARCH_WIDTH = 640
const CONVERSATION_WIDTH = 1216
const DIALOG_CHROME_INSET = 96

const CONTENT_COLUMN_MAX_WIDTH = 728
const HEADER_HEIGHT = 64
const SEARCH_INPUT_ROW_HEIGHT = 56

interface IConversationAware {
  $isConversation: boolean
}

const CHAT_COLUMN_MIN_WIDTH = 360

export const GlobalCommandModalShellStyled = styled.div<IConversationAware>`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: stretch;

  width: ${({ $isConversation }) =>
    $isConversation ? `${CONVERSATION_WIDTH}px` : `${SEARCH_WIDTH}px`};
  height: ${({ $isConversation, theme }) =>
    $isConversation
      ? `calc(100vh - ${theme.typography.pxToRem(DIALOG_CHROME_INSET)})`
      : 'auto'};
  margin: 0;
  max-width: 100%;
  max-height: calc(
    100vh - ${(props) => props.theme.typography.pxToRem(DIALOG_CHROME_INSET)}
  );

  transition:
    width ${TRANSITION},
    height ${TRANSITION};

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`

export const GlobalCommandModalRowStyled = styled.div`
  flex: 1 1 auto;
  min-height: 0;

  display: flex;
  flex-direction: row;
  align-items: stretch;
`

export const GlobalCommandModalMainStyled = styled.div`
  flex: 1 1 0;
  min-width: ${(props) =>
    props.theme.typography.pxToRem(CHAT_COLUMN_MIN_WIDTH)};
  min-height: 0;

  display: grid;
  grid-template-areas:
    'body'
    'footer';
  grid-template-rows: 1fr auto;
  grid-template-columns: minmax(0, 1fr);
`

export const GlobalCommandModalHeaderStyled = styled.div`
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${(props) => props.theme.space.small};
  box-sizing: border-box;
  min-height: ${(props) => props.theme.typography.pxToRem(HEADER_HEIGHT)};
  padding: ${(props) => props.theme.space.medium}
    ${(props) => props.theme.typography.pxToRem(24)};
  border-bottom: 1px solid
    ${(props) => props.theme.colors.content.secondaryBorder};
`

export const GlobalCommandModalHeaderContentStyled = styled.div`
  display: flex;
  align-items: center;
  gap: ${(props) => props.theme.space.small};
  flex: 1;
  min-width: 0;
`

// Figma "AI logo": gradient tile behind the sparkle; the end colour has no
// theme token.
export const GlobalCommandModalLogoStyled = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: ${(props) => props.theme.typography.pxToRem(32)};
  height: ${(props) => props.theme.typography.pxToRem(32)};
  border-radius: ${(props) => props.theme.typography.pxToRem(8)};
  background: ${(props) => props.theme.colors.globalCommand.accent};
`

export const GlobalCommandModalTitleStyled = styled.h2`
  margin: 0;
  min-width: 0;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  font-family: ${(props) => props.theme.fontFamily.header};
  font-size: ${(props) => props.theme.typography.pxToRem(14)};
  font-weight: ${(props) => props.theme.fontWeights.semibold};
  line-height: ${(props) => props.theme.typography.pxToRem(24)};
  color: ${(props) => props.theme.colors.text.primary};
`

export const GlobalCommandModalCloseAreaStyled = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  z-index: 2;
  display: flex;
  align-items: center;
  height: ${(props) => props.theme.typography.pxToRem(SEARCH_INPUT_ROW_HEIGHT)};
  padding: 0 ${(props) => props.theme.typography.pxToRem(8)};
`

export const GlobalCommandModalBodyStyled = styled.div<IConversationAware>`
  grid-area: body;
  min-height: 0;

  overflow-y: auto;

  ${({ $isConversation, theme }) =>
    $isConversation &&
    css`
      scroll-padding-bottom: calc(${theme.space.medium} * 2);
      scrollbar-width: none;

      &::-webkit-scrollbar {
        display: none;
      }
    `}
`

export const GlobalCommandModalBodyContentStyled = styled.div<IConversationAware>`
  ${({ $isConversation, theme }) =>
    $isConversation &&
    css`
      max-width: ${theme.typography.pxToRem(CONTENT_COLUMN_MAX_WIDTH)};
      margin: 0 auto;
      padding: ${theme.space.large} calc(${theme.space.medium} * 2)
        calc(${theme.space.medium} * 2);
    `}
`

export const GlobalCommandModalFooterStyled = styled.div`
  grid-area: footer;
  padding: 0 ${(props) => props.theme.space.medium}
    ${(props) => props.theme.space.medium};
  display: flex;
  justify-content: center;
`

export const GlobalCommandModalFooterContentStyled = styled.div`
  width: 100%;
  max-width: ${(props) =>
    props.theme.typography.pxToRem(CONTENT_COLUMN_MAX_WIDTH)};
  padding: 0 calc(${(props) => props.theme.space.medium} * 2);
`
